import type { RawTransaction } from '@/storage/repo';
import { categorize, merchantKey } from '@/parse/categorize';
import type { Category } from '@/app/format';

export interface ProposedExpense {
  name: string;
  amount: number;        // mean of the last 3 months
  category: Category;
  occurrences: number;    // # of times seen in the window
  lastDate: string;
  median: number;
  stdDev: number;        // % of mean — high stdDev = variable
  stable: boolean;       // stdDev < 25%
  transactions: RawTransaction[];
}

const WINDOW_DAYS = 90;
const RECURRING_MIN = 2;
const STABLE_STDDEV_PCT = 0.25;

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = xs.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function stdDevPct(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((s, x) => s + x, 0) / xs.length;
  if (mean === 0) return 0;
  const variance = xs.reduce((s, x) => s + (x - mean) ** 2, 0) / xs.length;
  return Math.sqrt(variance) / Math.abs(mean);
}

/**
 * Group transactions by normalised merchant key, take recurring
 * (≥2 occurrences in last 90 days), and propose a monthly budget item.
 */
export function buildSuggestions(txs: RawTransaction[]): ProposedExpense[] {
  const now = Date.now();
  const cutoff = now - WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const inWindow = txs.filter(t => new Date(t.postedAt).getTime() >= cutoff && t.amountCents < 0);

  const groups = new Map<string, RawTransaction[]>();
  for (const t of inWindow) {
    const key = merchantKey(t.description);
    if (!key || key.length < 3) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(t);
  }

  const out: ProposedExpense[] = [];
  for (const [, items] of groups) {
    if (items.length < RECURRING_MIN) continue;
    const amounts = items.map(t => Math.abs(t.amountCents) / 100);
    const mean = amounts.reduce((s, x) => s + x, 0) / amounts.length;
    const med  = median(amounts);
    const sdev = stdDevPct(amounts);
    const stable = sdev < STABLE_STDDEV_PCT;
    const sorted = items.slice().sort((a, b) => b.postedAt.localeCompare(a.postedAt));
    const lastDate = sorted[0].postedAt;
    const cat = categorize(sorted[0].description) || 'Living Allocations';
    // Pretty name: first non-numeric token sequence
    const name = prettyName(sorted[0].description);
    out.push({
      name,
      amount: Math.round(mean * 100) / 100,
      category: cat,
      occurrences: items.length,
      lastDate,
      median: Math.round(med * 100) / 100,
      stdDev: sdev,
      stable,
      transactions: items,
    });
  }

  return out.sort((a, b) => b.amount * b.occurrences - a.amount * a.occurrences);
}

function prettyName(desc: string): string {
  // Capitalise, collapse spaces
  const cleaned = desc.replace(/\s+/g, ' ').trim();
  return cleaned
    .split(' ')
    .map(w => w.length > 2 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w.toUpperCase())
    .join(' ')
    .slice(0, 64);
}
